import "../category.scss"; //import styles from "./Category.module.scss" if you want to use module
import "react-input-range/lib/css/index.css";

import { PageProps, graphql } from "gatsby";
import React, { useContext, useEffect, useState } from "react";

import CategoryFilters from "./CategoryFilters";
import CategoryFiltersMobile from "./CategoryFiltersMobile";
import { LocalType } from "../../models/localType";
import { SEOArguments } from "../../classes/seoArguments";
import { useMemo } from "react";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { useTranslation } from "react-i18next";
import SEO from "../../components/Seo";
import ProductComparator from "../../components/productListing/ProductComparator";
import { CategoryProductListing } from "../../components/productListing";
import MainLayout from "../../components/layout/MainLayout";
import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services/dist/src/contexts/servicesContext";
import { IProduct } from "@sbi-international/boutik.gatsby.plugin.services";
import { ProductReviewsResume } from "../../models/productReviewsResume";

export const query = graphql`
    query CategoryTemplate($category_id: String) {
        allProductsJson(
            filter: { category: { eq: $category_id }, status: { eq: "actif" } }
            sort: { attributes: { size: { values: { en_CA: DESC } } } }
        ) {
            nodes {
                id
                code
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                images
                fields {
                    ...productsFieldsImagesFragment
                }
                attributes {
                    PWMaSoi {
                        group
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    Surfi {
                        values {
                            en_CA
                            fr_CA
                            en_US
                        }
                    }
                    size {
                        values {
                            en_CA
                            fr_CA
                            en_US
                        }
                    }
                    available_country {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    certifications {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    MaiMob {
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    Alcove {
                        values {
                            en_US
                            fr_CA
                            en_CA
                        }
                    }
                    EPA {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        metadataStoves: allProductsJson(filter: { code: { eq: "century_poeles" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        metadataInserts: allProductsJson(filter: { code: { eq: "century_encastrables" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        allProductReviewsJson {
            nodes {
                sku
                average
                count
            }
        }
    }
`;

function isbetween(x: any, min: any, max: any) {
    return x >= min && x <= max;
}

function validateProduct(product: any, area: any, power: any, currentLocale: any) {
    const productPower = parseInt(
        product.attributes?.PWMaSoi?.values[0]?.en_CA.split(" ")[0].replace(",", "")
    );
    const productMinArea = parseInt(product.attributes?.Surfi?.values[0]?.en_CA);
    const productMaxArea = product.attributes?.Surfi?.values[0]?.en_CA
        .split("-")[1]
        .replace(",", "");

    const available_countries = product.attributes?.available_country?.values;

    const current_country = currentLocale.split("_")[1] === "CA" ? "Canada" : "United States";

    const is_available_current_country =
        available_countries === undefined
            ? true
            : available_countries.some(
                  (country: any) => country[currentLocale] === current_country
              );

    const minArea = area?.min;
    const maxArea = area?.max;
    const minPower = power?.min;
    const maxPower = power?.max;

    return (
        is_available_current_country &&
        productPower >= minPower &&
        productPower <= maxPower &&
        ((productMinArea < minArea && productMaxArea > maxArea) ||
            isbetween(productMinArea, minArea, maxArea) ||
            isbetween(productMaxArea, minArea, maxArea))
    );
}

function filterBySize(product: any, size: any) {
    const productSize = product?.attributes?.size?.values[0]["en_CA"];
    const small = size[0];
    const medium = size[1];
    const large = size[2];
    const xlarge = size[3];

    if (small === false && medium === false && large === false && xlarge === false) return product;

    if (productSize === "Small" && small === true) {
        return product;
    } else if (productSize === "Medium" && medium === true) {
        return product;
    } else if (productSize === "Large" && large === true) {
        return product;
    } else if (productSize === "Extra Large" && xlarge === true) {
        return product;
    }
}

function filterByMadeIn(product: IProduct, madeIn: any) {
    const liveMadeInCanada = product?.isMadeInCanada ?? false;
    const liveMadeInUSA = product?.isMadeInUSA ?? false;
    const canada = madeIn[0];
    const usa = madeIn[1];

    if (canada === false && usa === false) return product;

    if (liveMadeInCanada && canada) {
        return product;
    } else if (liveMadeInUSA && usa) {
        return product;
    }
}

function filterByCertified(product: any, certified: any) {
    const maimob = certified[0];
    const alcove = certified[1];
    const epa = certified[2];
    if (!maimob && !alcove && !epa) {
        return true;
    }
    if (
        maimob &&
        product?.attributes?.MaiMob?.values?.[0]?.["fr_CA"]?.toLowerCase()?.includes("oui")
    ) {
        return true;
    }
    if (
        alcove &&
        product?.attributes?.Alcove?.values?.[0]?.["fr_CA"]?.toLowerCase()?.includes("oui")
    ) {
        return true;
    }
    if (
        epa &&
        product?.attributes?.certifications?.values?.[0]?.["fr_CA"]
            ?.toLowerCase()
            ?.includes("epa-2020")
    ) {
        return true;
    }

    return false;
}

function filterByPrices(product: any, price: any, priceRange: any) {
    const minRange = priceRange.min;
    const maxRange = priceRange.max;
    return price <= maxRange && price >= minRange;
}

export default function Category({
    data,
    pageContext: { category_name },
}: PageProps<Queries.CategoryTemplateQuery, CategoryTemplateContext>) {
    const { t } = useTranslation("templatePages");
    const { currentLocale } = usePageLocalization();

    const [productsData, setProductsData] = useState<IProduct[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<any[]>([]);

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    const productReviewsResume: ProductReviewsResume[] = data?.allProductReviewsJson?.nodes
        ? data.allProductReviewsJson.nodes
              .filter((node) => node.sku !== null && node.average !== null && node.count !== null)
              .map((node) => ({
                  sku: node.sku as string,
                  average: node.average as number,
                  count: node.count as number,
              }))
        : [];

    const families = data.allProductsJson.nodes.filter((product: any) => {
        const country = currentLocale.includes("CA") ? "Canada" : "United States";
        const isAvailable = product.attributes?.available_country?.values?.some(
            (v: any) => v && v["en_CA"] === country
        );
        return isAvailable;
    });

    const [area, setArea] = useState({ min: 0, max: 2700 });
    const [power, setPower] = useState({ min: 0, max: 120000 });
    const [priceRange, setPriceRange] = useState({ min: 0, max: 3000 });
    const [size, setSize] = useState([false, false, false, false]);
    const [madeIn, setMadeIn] = useState([false, false, false, false]);
    const [certified, setCertified] = useState([false, false, false]);

    const [compareArray, setCompareArray] = useState<any[]>([]);

    useEffect(() => {
        let filtered = families.filter((product) =>
            validateProduct(product, area, power, currentLocale)
        );
        productService.operations
            .getProducts(filtered.map((p) => p.code).filter((code) => code !== null))
            .then((products) => {
                setProductsData(products);
            });
    }, []);

    useEffect(() => {
        if (productsData.length > 0) {
            let filtered = families.filter((product) =>
                validateProduct(product, area, power, currentLocale)
            );

            
            filtered = filtered.filter((product) => {
                if (!product.code) return true;
                const productLive = productsData.find((p) => p.sku === product.code);
                const price = productLive?.currentPrice?.finalPrice?.value;

                if (price === undefined) return true;
                return filterByPrices(product, price, priceRange);
            });

            filtered = filtered.filter((product) => filterBySize(product, size));
            filtered = filtered.filter((product, index) => filterByCertified(product, certified));

            filtered = filtered.filter((product) => {
                const productLive = productsData.find((p) => p.sku === product.code);
                if (productLive === undefined) return true;
                return filterByMadeIn(productLive, madeIn);
            });
         

            setFilteredProducts(filtered);
            console.log(certified);
        }
    }, [productsData, area, power, currentLocale, priceRange, size, certified, madeIn]);

    const seoArgumentsStoves = new SEOArguments(
        data.metadataStoves,
        currentLocale as keyof LocalType
    );

    const seoArgumentsInserts = new SEOArguments(
        data.metadataInserts,
        currentLocale as keyof LocalType
    );

    return (
        <MainLayout>
            {category_name?.["en_CA"] === "Wood Stoves" ? (
                <SEO
                    title={seoArgumentsStoves.title}
                    description={seoArgumentsStoves.description}
                />
            ) : (
                <SEO
                    title={seoArgumentsInserts.title}
                    description={seoArgumentsInserts.description}
                />
            )}
            <div className="container">
                <div className="row">
                    <aside className="categoryFilters" id="secondary">
                        <CategoryFilters
                            category={category_name}
                            families={families}
                            setArea={setArea}
                            setPower={setPower}
                            setPrice={setPriceRange}
                            setSize={setSize}
                            size={size}
                            madeIn={madeIn}
                            setMadeIn={setMadeIn}
                            certified={certified}
                            setCertified={setCertified}
                            currentLocale={currentLocale}
                        />
                    </aside>

                    <div id="primary">
                        <header>
                            <div className="container">
                                <h1>
                                    {category_name?.["en_CA"] === "Wood Stoves"
                                        ? t("stove-title")
                                        : t("insert-title")}
                                </h1>
                            </div>
                        </header>

                        <CategoryFiltersMobile
                            setArea={setArea}
                            setPower={setPower}
                            setPrice={setPriceRange}
                            setSize={setSize}
                            setMadeIn={setMadeIn}
                            area={area}
                            power={power}
                            price={priceRange}
                            size={size}
                            madeIn={madeIn}
                            currentLocale={currentLocale}
                        />

                        <CategoryProductListing
                            isComparable={true}
                            setCompareArray={setCompareArray}
                            compareArray={compareArray}
                            columns="2"
                            products={filteredProducts}
                            reviews={productReviewsResume}
                            scroll={false}
                        />

                        {compareArray.length > 0 && (
                            <ProductComparator
                                compareArray={compareArray}
                                setCompareArray={setCompareArray}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}
